/**
 * Per-route design superelevation (`e_max`) for Horizontal Curve Analysis.
 *
 * The user types a PERCENT per Route XML on the HCA card ("6" for 6%); the
 * pipeline consumes a FRACTION (0.06). The conversion happens here, once, on the
 * way into the run payload — `Backend/Services/Horizontal/HCurves_detection/
 * Superelevation.py` feeds `e_max` straight into Rmin / Rpi / Hpi, so a percent
 * that reached it undivided would mean 600% and silently wreck every radius
 * check and safety score.
 *
 * The map is keyed by Route XML FILE NAME because that is the only identifier
 * both ends share: the backend joins on `route_entry["xml_name"]`
 * (chain._build_horizontal_payload); the Drive file id the GUI keys its matrix
 * by never reaches it.
 *
 * Routes the user did not set a value for are OMITTED rather than defaulted, so
 * the backend's own precedence (template scrape → 0.06) still decides. See
 * Backend/Pipeline/tests/test_engine_v2_horizontal_e_max.py.
 */

/** The HCA task label the routes matrix keys its columns by. */
const HCA_TASK_OPTION = "Horizontal Curve Analysis (HCA)";

/** AASHTO design superelevation, as a percent. 6% is the usual value. */
export const DEFAULT_E_MAX_PERCENT = 6;

/**
 * Plausible band, in percent. AASHTO tops out at 12%; anything at or below zero
 * divides by zero in `Rpi = Vr**2 / (15 * eMax)`.
 */
export const E_MAX_PERCENT_MIN = 0;
export const E_MAX_PERCENT_MAX = 15;

/** The typed value as a usable percent, or null when it cannot be trusted. */
export const normalizeEMaxPercent = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const num = Number(value);
  if (!Number.isFinite(num)) return null;
  if (num <= E_MAX_PERCENT_MIN || num > E_MAX_PERCENT_MAX) return null;
  return num;
};

/**
 * `{ [routeXmlFileName]: eMaxFraction }` for the routes assigned to HCA.
 *
 * @param {object}   input
 * @param {Array}    input.routesXmlFiles        [{googleDriveId, name}]
 * @param {object}   input.routesMatrixSelection {[driveId]: {[taskName]: boolean}}
 * @param {object}   input.eMaxPercentByRoute    {[driveId]: percent as typed}
 * @param {string}   [input.taskOption]          the HCA task label
 */
export const buildEMaxByRouteXml = ({
  routesXmlFiles = [],
  routesMatrixSelection = {},
  eMaxPercentByRoute = {},
  taskOption = HCA_TASK_OPTION,
}) => {
  const out = {};
  for (const routeFile of routesXmlFiles || []) {
    const driveId = String(routeFile?.googleDriveId ?? "").trim();
    const xmlName = String(routeFile?.name ?? "").trim();
    if (!driveId || !xmlName) continue;
    if (!routesMatrixSelection?.[driveId]?.[taskOption]) continue;
    const percent = normalizeEMaxPercent(eMaxPercentByRoute[driveId]);
    if (percent === null) continue;
    // Round to shed IEEE754 noise: 7.3 / 100 is 0.07299999999999999.
    out[xmlName] = Number((percent / 100).toFixed(6));
  }
  return out;
};

/**
 * What the HCA card header reports while collapsed: how many ticked routes
 * carry their own e_max. A value counts only when the payload would actually
 * send it (same normalizeEMaxPercent gate as buildEMaxByRouteXml), so the header
 * can never claim a route is customised when the run will use the default.
 *
 * @returns {{state: "default"|"partial"|"all", customCount: number, assignedCount: number}}
 */
export const describeEMaxStatus = ({
  routesXmlFiles = [],
  routesMatrixSelection = {},
  eMaxPercentByRoute = {},
  taskOption = HCA_TASK_OPTION,
}) => {
  let assignedCount = 0;
  let customCount = 0;
  for (const routeFile of routesXmlFiles || []) {
    const driveId = String(routeFile?.googleDriveId ?? "").trim();
    if (!driveId || !routesMatrixSelection?.[driveId]?.[taskOption]) continue;
    assignedCount += 1;
    if (normalizeEMaxPercent(eMaxPercentByRoute[driveId]) !== null) customCount += 1;
  }
  const state =
    customCount === 0 ? "default" : customCount === assignedCount ? "all" : "partial";
  return { state, customCount, assignedCount };
};

/**
 * The `horizontal_data` object a run payload sends. Every run path builds it
 * here — the single-run Run button (handleSubmit) and the per-route
 * multi-route runner (runSelectedRoutes) — so a typed e_max cannot be dropped
 * by one of them again. `xmlFiles` is the set of Route XMLs THIS run covers
 * (the whole project for a single run, one route's files for a multi-route
 * run); only those that are ticked for HCA with a usable value make it in.
 * Any other keys already on `horizontalData` are kept.
 */
export const buildHorizontalData = ({
  horizontalData = null,
  xmlFiles = [],
  routesMatrixSelection = {},
  eMaxPercentByRoute = {},
  taskOption = HCA_TASK_OPTION,
}) => {
  const base =
    horizontalData && typeof horizontalData === "object" ? horizontalData : {};
  const eMaxByRouteXml = buildEMaxByRouteXml({
    routesXmlFiles: xmlFiles,
    routesMatrixSelection,
    eMaxPercentByRoute,
    taskOption,
  });
  return Object.keys(eMaxByRouteXml).length > 0
    ? { ...base, e_max_by_route_xml: eMaxByRouteXml }
    : base;
};
